"use client";

import { APP_UPDATE_AVAILABLE_EVENT } from "@/lib/app-update-events";
import {
  isLessonRefreshBlocked,
  requestAppRefresh,
  subscribeLessonRefreshGuard,
} from "@/lib/lesson-refresh-guard";
import { createLogger } from "@/lib/logger";
import { getAppVersion } from "@/lib/version";
import { X } from "lucide-react";
import { useEffect, useId, useState, useSyncExternalStore } from "react";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
const RECENT_UPDATE_NOTICE_MS = 5 * 60 * 1000;
const MAX_OBSERVATION_GAP_MS = CHECK_INTERVAL_MS + 60 * 1000;
const logger = createLogger("VersionUpdater");

export function VersionUpdater() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showConnectionDelayNote, setShowConnectionDelayNote] = useState(false);
  const refreshBlocked = useSyncExternalStore(
    subscribeLessonRefreshGuard,
    isLessonRefreshBlocked,
    () => false
  );
  const refreshHelpId = useId();

  useEffect(() => {
    const current = getAppVersion();
    let lastObservedVersion: string | null = null;
    let lastCheckedAt = 0;
    let isChecking = false;
    let recheckRequested = false;
    let disposed = false;
    let noticeExpiresAt = 0;
    let noticeTimeout: ReturnType<typeof setTimeout> | undefined;

    const checkVersion = async () => {
      if (disposed) return;
      // Browser timers can be suspended while a device sleeps. Expire the
      // caution by wall-clock time on resume, even before a request completes.
      if (noticeExpiresAt && Date.now() >= noticeExpiresAt) {
        setShowConnectionDelayNote(false);
        clearTimeout(noticeTimeout);
        noticeExpiresAt = 0;
      }
      if (isChecking) {
        recheckRequested = true;
        return;
      }
      isChecking = true;
      try {
        // Fetch version from server
        // Add timestamp to prevent browser caching of the check itself
        const res = await fetch(`/api/version?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;

        const data = await res.json();
        const serverVersion = data.version;
        if (disposed || typeof serverVersion !== "string" || !serverVersion) return;
        const checkedAt = Date.now();

        const outdated = serverVersion !== current;
        setHasUpdate(outdated);
        if (!outdated) {
          setShowConnectionDelayNote(false);
          clearTimeout(noticeTimeout);
          noticeExpiresAt = 0;
        }

        // An initial mismatch may be hours old. Only a transition observed
        // across recent checks suggests a rollout may still affect new calls.
        // This is a bounded caution, not a claim about Render deployment health.
        if (
          outdated &&
          lastObservedVersion !== null &&
          serverVersion !== lastObservedVersion &&
          checkedAt - lastCheckedAt <= MAX_OBSERVATION_GAP_MS
        ) {
          setShowConnectionDelayNote(true);
          clearTimeout(noticeTimeout);
          noticeExpiresAt = checkedAt + RECENT_UPDATE_NOTICE_MS;
          noticeTimeout = setTimeout(() => {
            setShowConnectionDelayNote(false);
            noticeExpiresAt = 0;
          }, RECENT_UPDATE_NOTICE_MS);
        }
        lastObservedVersion = serverVersion;
        lastCheckedAt = checkedAt;
      } catch (err) {
        logger.debug("Version check unavailable", { error: String(err) });
      } finally {
        isChecking = false;
        if (recheckRequested && !disposed) {
          recheckRequested = false;
          void checkVersion();
        }
      }
    };

    // A worker can be replaced after a fresh, already-current page loads.
    // Treat the event as a hint to verify the page's version, never as proof.
    const onWorkerUpdate = () => void checkVersion();
    window.addEventListener(APP_UPDATE_AVAILABLE_EVENT, onWorkerUpdate);

    // Check immediately and then periodically
    void checkVersion();

    // Check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);

    // Check on window focus
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(interval);
      clearTimeout(noticeTimeout);
      disposed = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, onWorkerUpdate);
    };
  }, []);

  if (!hasUpdate || isDismissed) return null;

  return (
    <div
      role="status"
      className="fixed top-0 left-0 right-0 z-50 bg-[#FD954F] text-[#1C1C1E] px-4 py-2 flex flex-col sm:flex-row items-start justify-between gap-3 shadow-md"
    >
      <div className="text-sm flex-1">
        <p className="font-medium">
          <strong>A fresh update is ready!</strong> You can refresh after your lesson. If something
          isn’t working, try refreshing and reconnecting.
        </p>
        {showConnectionDelayNote && (
          <p>
            We’re updating ChickyTutor, so you might notice a brief interruption. Thanks for your
            patience!
          </p>
        )}
      </div>
      <div className="flex w-full sm:w-auto items-start justify-between gap-2 sm:shrink-0">
        <div className="flex flex-wrap sm:flex-col items-center sm:items-end gap-2 sm:gap-1">
          <button
            type="button"
            onClick={() => requestAppRefresh()}
            disabled={refreshBlocked}
            aria-describedby={refreshBlocked ? refreshHelpId : undefined}
            className="rounded bg-white px-3 py-2 text-sm font-semibold hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Refresh now
          </button>
          {refreshBlocked && (
            <span id={refreshHelpId} className="sm:max-w-28 sm:text-right text-xs">
              Available after your lesson
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsDismissed(true)}
          className="p-2 hover:bg-black/10 rounded-full transition-colors"
          aria-label="Dismiss update notice"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
